import "./JoinUs.css";
import { useState, useMemo } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { Link } from "react-router-dom";

import Select from "react-select";
import { City } from "country-state-city";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

export default function JoinUs() {
    const [formData, setFormData] = useState({
        firstName: "",
        email: "",
        phone: "",
        city: "",
        status: "",
    });

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    // 🎨 Backgrounds dynamiques selon le status
    const statusBackgrounds = {
        dating: "bg-offred",
        single: "bg-deepgreen",
        dtf: "bg-genyellow",
        situationship: "bg-offblue",
        default: "bg-offblack",
    };

    // 🌍 Pays Afrique de l’Ouest
    const WEST_AFRICA_COUNTRIES = [
        "BJ", "BF", "CV", "CI", "GM",
        "GH", "GN", "GW", "LR", "ML",
        "NE", "NG", "SN", "SL", "TG"
    ];

    // 🌍 Villes Afrique de l’Ouest (accent-insensitive)
    const cityOptions = useMemo(() => {
        return City.getAllCities()
            .filter((city) =>
                WEST_AFRICA_COUNTRIES.includes(city.countryCode)
            )
            .map((city) => ({
                value: `${city.name}, ${city.countryCode}`,
                label: `${city.name} (${city.countryCode})`,
            }));
    }, []);

    const normalize = (str) =>
        str
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await addDoc(collection(db, "lafriendzone-expansion-leads"), {
                ...formData,
                createdAt: serverTimestamp(),
                source: "QR Card",
            });

            setSuccess(true);
            setFormData({
                firstName: "",
                email: "",
                phone: "",
                city: "",
                status: "",
            });
        } catch (error) {
            console.error("Error adding document:", error);
        } finally {
            setLoading(false);
        }
    };

    const backgroundClass =
        statusBackgrounds[formData.status] || statusBackgrounds.default;

    return (
        <div
            className={`flex items-center justify-center rounded-2xl px-6 py-6 transition-colors duration-500 ${backgroundClass}`}
        >
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-5 sm:p-6 md:p-8">
                {!success ? (
                    <>
                        <h1 className="text-xl sm:text-2xl font-bold text-center mb-2">
                            You’re on the list 👀
                        </h1>

                        <p className="text-center text-sm text-gray-500 mb-6">
                            LaFriendzone is expanding.
                            <br />
                            Drop your details and take a chance to be happy.
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Status */}
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-black"
                            >
                                <option value="">Relationship status*</option>
                                <option value="dating">I have a partner</option>
                                <option value="single">I'm Single</option>
                                <option value="situationship">It's complicated</option>
                                <option value="dtf">Available Only Tonight</option>

                            </select>

                            {/* First name */}
                            <input
                                type="text"
                                name="firstName"
                                placeholder="First name*"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black"
                            />

                            {/* Email */}
                            <input
                                type="email"
                                name="email"
                                placeholder="Email address*"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black"
                            />

                            {/* 📞 Phone input with country dropdown */}
                            <PhoneInput
                                country={"tg"}
                                value={formData.phone}
                                onChange={(phone) =>
                                    setFormData({
                                        ...formData,
                                        phone: `+${phone}`,
                                    })
                                }
                                inputProps={{
                                    required: true,
                                    name: "phone",
                                }}
                                inputStyle={{
                                    width: "100%",
                                    height: "48px",
                                    borderRadius: "0.75rem",
                                    border: "1px solid #e5e7eb",
                                    color: "#000000",          // ✅ TEXTE NOIR (FIX)
                                    backgroundColor: "#ffffff" // ✅ FOND BLANC (SÉCURITÉ)
                                }}
                                buttonStyle={{
                                    borderRadius: "0.75rem 0 0 0.75rem",
                                }}
                                containerStyle={{
                                    width: "100%",
                                }}
                            />


                            {/* 🌍 City selector */}
                            <Select
                                placeholder="Search your city*"
                                options={cityOptions}
                                isSearchable
                                filterOption={(option, inputValue) =>
                                    normalize(option.label).includes(normalize(inputValue))
                                }
                                noOptionsMessage={() =>
                                    "City not found? Select the closest one."
                                }
                                onChange={(selected) =>
                                    setFormData({
                                        ...formData,
                                        city: selected?.value || "",
                                    })
                                }
                            />

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-deepgreen text-white py-3 rounded-xl font-semibold hover:opacity-90 transition"
                            >
                                {loading ? "Saving..." : "Save my spot"}
                            </button>

                            <p className="text-xs text-center text-gray-400">
                                No spam. Just vibes & early access.
                            </p>
                        </form>
                    </>
                ) : (
                    <div className="text-center py-10">
                        <h2 className="text-xl sm:text-2xl font-bold mb-3">
                            You’re in 🎉
                        </h2>

                        <p className="text-gray-600 mb-6">
                            Your spot is saved.
                            <br />
                            We’ll contact you soon.
                        </p>

                        <div className="flex justify-center">
                            <button
                                onClick={() => {
                                    setSuccess(false);
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}

                                className="bg-black text-white px-6 py-3 rounded-xl font-semibold hover:opacity-90 transition"
                            >
                                Back to form
                            </button>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
}
